/**
 * @fileoverview AipAssistFooter — bottom input row of the AIP Assist
 * sidebar. Auto-resizing textarea ("Ask a question…") with a right-side
 * button group (microphone for audio transcription), and beneath it a
 * small minimal "Mode: <mode>" button that opens a mode-picker popover.
 *
 * Foundry mapping: `aip-assist-impl__footer` → `__input-container` /
 * `__input` (with a `bp6-text-area-auto-resize` textarea) + `__button-group`
 * (mic + future tools); beneath, an `__small-button` Mode chevron-up
 * button that anchors the mode-picker listbox.
 */

import type { ReactElement, ReactNode } from "react";
import { Button, TextArea } from "@blueprintjs/core";

interface Props {
  value: string;
  onValueChange: (v: string) => void;
  onSubmit: () => void;
  placeholder?: string;
  mode: string;
  onPickMode?: () => void;
  /** Extra trailing actions inside the textarea button group (e.g. mic). */
  textareaActions?: ReactNode;
  className?: string;
}

export function AipAssistFooter({
  value,
  onValueChange,
  onSubmit,
  placeholder = "Ask a question…",
  mode,
  onPickMode,
  textareaActions,
  className,
}: Props): ReactElement {
  return (
    <div data-testid="aip-assist-footer" className={["bp-aip-footer", className].filter(Boolean).join(" ")}>
      <div className="bp-aip-footer-input-container">
        <div className="bp-aip-footer-input">
          <TextArea
            data-testid="aip-assist-footer-input"
            autoResize
            placeholder={placeholder}
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSubmit();
              }
            }}
            className="bp-aip-footer-textarea"
          />
          <div className="bp-aip-footer-button-group">
            {textareaActions ?? (
              <Button data-testid="aip-assist-footer-mic" minimal icon="microphone" aria-label="Record audio" />
            )}
          </div>
        </div>
        <div>
          <Button
            data-testid="aip-assist-footer-mode"
            small
            minimal
            icon="chevron-up"
            onClick={onPickMode}
            className="bp-aip-footer-mode-button"
          >
            Mode: {mode}
          </Button>
        </div>
      </div>
    </div>
  );
}
